import {createSelector} from 'reselect'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import {productsByCategory} from './plannerView'
import {currentCategory, productsBySlug} from './categoryView'
import {
  ProductCompatibilityTags,
  ProductIncompatibilityAnalyticsMessage,
  ProductIncompatibilityMessage,
} from './enums'

const detailView = (state) => get(state, 'lookbuilder.detailView')

export const currentProductSlug = (state) => get(detailView(state), 'currentProductSlug')

/**
 * Uses the current product slug to retrieve details from the Category View's state
 *
 * @return {Object|undefined} A product object or undefined if it doesn't exist
 */
export const currentProduct = createSelector(
  currentProductSlug,
  productsBySlug,
  ($currentProductSlug, $productsBySlug) => $productsBySlug[$currentProductSlug],
)

/**
 * Returns the appropriate images for the Detail View Carousel by including only
 * product and model shots. Filters out `still-life` images.
 * @returns {Object[]} An array of product image objects
 */
export const currentProductCarouselImages = createSelector(currentProduct, ({setImages}) =>
  setImages.filter(({contentType}) => ['product', 'model'].includes(contentType)),
)

/**
 * A helper function for streamlining creation of an incompatibility object
 *
 * @param {String} category The category of the incompatible product
 * @param {String} messageConstant A key in the `ProductIncompatibilityMessage` object
 * @param {Object} product The actual incompatible product object
 * @returns {Object} Properly formatted incompatibility object
 */
function createIncompatibilityObject(category, messageConstant, product) {
  const incompatibilityMessage = ProductIncompatibilityMessage[messageConstant]
  const analyticsMessage = ProductIncompatibilityAnalyticsMessage[messageConstant]

  if (incompatibilityMessage === undefined) {
    throw new Error('No Incompatibility Message defined')
  }

  return {analyticsMessage, category, incompatibilityMessage, incompatibleProduct: {...product}}
}

/**
 * Functional rule check to validate product compatibility between a suits-tuxedo and a
 * belt-suspenders product.
 *
 * @param {Object} suitsTuxedoProduct Object representing a suit or tuxedo product.
 * @param {Object} beltSuspendersProduct Object representing a belt or suspenders product.
 * @returns {Boolean} Determines if suit or tuxedo is compatible with a belt.
 */
function isSuitsTuxedoProductBeltCompatible(suitsTuxedoProduct, beltSuspendersProduct) {
  // checks for case when user adds a suit/tuxedo to a look without a belt/suspender and vice versa
  if (isEmpty(suitsTuxedoProduct) !== isEmpty(beltSuspendersProduct)) {
    return true
  }

  if (beltSuspendersProduct.subcategories.includes('suspenders')) {
    return true
  }

  const {childSet: pantProduct} = suitsTuxedoProduct.setChildren.find(({childSet}) =>
    childSet.subcategories.includes('pants'),
  )

  return pantProduct.compatibilities.includes(ProductCompatibilityTags.BELT_COMPATIBLE)
}

/**
 * Functional rule check to validate product compatibility between a shirts and
 * cufflinks product.
 *
 * @param {Object} shirtsProduct Object representing a shirt product
 * @param {Object} cufflinksProduct Object representing a cufflink product
 * @returns {Boolean} Determines if shirt is compatible with cufflinks
 */
function isShirtsProductCufflinkCompatible(shirtsProduct, cufflinksProduct) {
  if (isEmpty(shirtsProduct) !== isEmpty(cufflinksProduct)) {
    return true
  }

  return shirtsProduct.compatibilities.includes(ProductCompatibilityTags.CUFFLINK_COMPATIBLE)
}

/**
 * Functional rule check to validate product compatibility between a shirt and
 * shirt stud product.
 *
 * @param {Object} shirtsProduct Object representing a shirt product
 * @param {Object} shirtStudsProduct Object representing a shirt stud product
 * @returns {Boolean} Determines if shirt is compatible with shirt studs
 */
function isShirtsProductShirtStudCompatible(shirtsProduct, shirtStudsProduct) {
  if (isEmpty(shirtsProduct) !== isEmpty(shirtStudsProduct)) {
    return true
  }

  return shirtsProduct.compatibilities.includes(ProductCompatibilityTags.SHIRT_STUD_COMPATIBLE)
}

/**
 * Returns list of incompatible products from user's current look based on the item selected
 * to be added in the lookbuilder detail view.
 *
 * @return {Object[]} An array of product objects from the users current look which are incompatible
 *                    with the newly added product.
 */
export const getIncompatibleProducts = createSelector(
  currentCategory,
  currentProduct,
  productsByCategory,
  ($currentCategory, $currentProduct, $productsByCategory) => {
    switch ($currentCategory) {
      case 'suits-tuxedos': {
        return isSuitsTuxedoProductBeltCompatible(
          $currentProduct,
          $productsByCategory['belts-suspenders'],
        )
          ? []
          : [
              createIncompatibilityObject(
                'belts-suspenders',
                'TUXEDO_BELT',
                $productsByCategory['belts-suspenders'],
              ),
            ]
      }
      case 'belts-suspenders': {
        return isSuitsTuxedoProductBeltCompatible(
          $productsByCategory['suits-tuxedos'],
          $currentProduct,
        )
          ? []
          : [
              createIncompatibilityObject(
                'suits-tuxedos',
                'TUXEDO_BELT',
                $productsByCategory['suits-tuxedos'],
              ),
            ]
      }
      case 'cufflinks': {
        return isShirtsProductCufflinkCompatible($productsByCategory.shirts, $currentProduct)
          ? []
          : [createIncompatibilityObject('shirts', 'SHIRT_CUFFLINKS', $productsByCategory.shirts)]
      }
      case 'studs': {
        return isShirtsProductShirtStudCompatible($productsByCategory.shirts, $currentProduct)
          ? []
          : [createIncompatibilityObject('shirts', 'SHIRT_STUD', $productsByCategory.shirts)]
      }
      case 'shirts': {
        const incompatibilities = []
        if (!isShirtsProductCufflinkCompatible($currentProduct, $productsByCategory.cufflinks)) {
          incompatibilities.push(
            createIncompatibilityObject(
              'cufflinks',
              'SHIRT_CUFFLINKS',
              $productsByCategory.cufflinks,
            ),
          )
        }
        if (!isShirtsProductShirtStudCompatible($currentProduct, $productsByCategory.studs)) {
          incompatibilities.push(
            createIncompatibilityObject('studs', 'SHIRT_STUD', $productsByCategory.studs),
          )
        }
        return incompatibilities
      }
      default:
        return []
    }
  },
)
